import React from 'react';
import { Box } from '@mui/material';

import { TableContainer, TableHeader } from '@occc-app/design-system';
import { Tab, Tabs } from '@occc/ui-design-system';
import { useTabQueryParam } from '@occc/utils-query-params';

import RetailerNetworkLocationsForMemberTable from './RetailerNetworkLocationsForMember';
import RetailerNetworkLocationGroupsForMemberTable from './RetailerNetworkLocationGroupsForMember';
import RetailerNetworksForMemberTable from './RetailerNetworksForMember';
import { MemberEmployeeDetailsTab } from './types';
import {
  MEMBER_EMPLOYEE_DETAILS_TAB_PARAM,
  MEMBER_EMPLOYEE_DETAILS_TABS,
  mapTabLabel,
} from './constants';

import { useMembershipEmployeeData } from '../../hooks/useMembershipEmployeeData';

const transitionStyle = 'transform 500ms cubic-bezier(.75, 0, .25, 1)';
const targetingBoxStyles = {
  transition: transitionStyle,
  overflow: 'hidden',
  width: '100%',
};

const commonStyles = (isActive: boolean) => ({
  transition: transitionStyle,
  width: '100%',
  overflow: 'hidden',
  maxHeight: isActive ? '100%' : '0px',
});

const locationsTabStyles = (isActive: boolean) => ({
  ...commonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(-100%, 0)',
});

type MemberEmployeeDetailsProps = {
  employeeData: ReturnType<typeof useMembershipEmployeeData>;
};

export const MemberEmployeeDetails = ({ employeeData }: MemberEmployeeDetailsProps) => {
  const defaultTab: MemberEmployeeDetailsTab = employeeData.isEmployeeLocationsTabVisible
    ? 'locations'
    : 'retailerNetworks';
  const [activeTab, setActiveTab] = useTabQueryParam<MemberEmployeeDetailsTab>(
    MEMBER_EMPLOYEE_DETAILS_TAB_PARAM,
    defaultTab,
    MEMBER_EMPLOYEE_DETAILS_TABS,
  );

  const handleTabChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: MemberEmployeeDetailsTab,
  ) => {
    setActiveTab(value);
  };

  return (
    <TableContainer>
      <TableHeader
        disableTitleTypography
        tabs={
          <Tabs value={activeTab} onChange={handleTabChange}>
            {employeeData.isEmployeeLocationsTabVisible && (
              <Tab
                value={'locations' satisfies MemberEmployeeDetailsTab}
                label={mapTabLabel['locations']}
              />
            )}
            {employeeData.isEmployeeLocationGroupsTabVisible && (
              <Tab
                value={'locationGroups' satisfies MemberEmployeeDetailsTab}
                label={mapTabLabel['locationGroups']}
              />
            )}
            {employeeData.isEmployeeRetailerNetworksTabVisible && (
              <Tab
                value={'retailerNetworks' satisfies MemberEmployeeDetailsTab}
                label={mapTabLabel['retailerNetworks']}
              />
            )}
          </Tabs>
        }
      />
      <Box style={targetingBoxStyles}>
        {employeeData.isEmployeeLocationsTabVisible && (
          <Box
            id={'locations' satisfies MemberEmployeeDetailsTab}
            style={locationsTabStyles(activeTab === 'locations')}
          >
            <RetailerNetworkLocationsForMemberTable
              employeeLocations={employeeData.employeeLocations}
            />
          </Box>
        )}
        {employeeData.isEmployeeLocationGroupsTabVisible && (
          <Box
            id={'locationGroups' satisfies MemberEmployeeDetailsTab}
            style={locationsTabStyles(activeTab === 'locationGroups')}
          >
            <RetailerNetworkLocationGroupsForMemberTable
              employeeLocationGroups={employeeData.employeeLocationGroups}
            />
          </Box>
        )}
        {employeeData.isEmployeeRetailerNetworksTabVisible && (
          <Box
            id={'retailerNetworks' satisfies MemberEmployeeDetailsTab}
            style={locationsTabStyles(activeTab === 'retailerNetworks')}
          >
            <RetailerNetworksForMemberTable
              employeeRetailerNetworks={employeeData.employeeRetailerNetworks}
            />
          </Box>
        )}
      </Box>
    </TableContainer>
  );
};
